// Estilos del centro de control. Clases con prefijo `cc-`, inyectadas con <style> por la
// pantalla. Paleta nocturna sobre los colores de marca (secondary 950/900, primary, red).

export const controlCenterCss = `
.cc {
  --bg: #0A0410;
  --panel: rgba(255, 255, 255, 0.035);
  --panel-strong: rgba(255, 255, 255, 0.06);
  --line: rgba(255, 255, 255, 0.08);
  --line-strong: rgba(255, 255, 255, 0.14);
  --text: #F5EFF9;
  --muted: rgba(245, 239, 249, 0.58);
  --faint: rgba(245, 239, 249, 0.34);
  --teal: #2AD2C5;
  --teal-soft: rgba(42, 210, 197, 0.14);
  --red: #FF3D6A;
  --red-soft: rgba(255, 61, 106, 0.14);
  --amber: #FFB547;
  --amber-soft: rgba(255, 181, 71, 0.14);
  --violet: #C574F1;
  --violet-soft: rgba(197, 116, 241, 0.16);

  position: relative;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 32px);
  min-height: 560px;
  border-radius: 22px;
  overflow: hidden;
  color: var(--text);
  font-family: 'Poppins_400Regular', system-ui, sans-serif;
  background:
    radial-gradient(1100px 520px at 0% -10%, rgba(127, 19, 185, 0.38), transparent 60%),
    radial-gradient(900px 480px at 100% -5%, rgba(42, 210, 197, 0.13), transparent 60%),
    radial-gradient(800px 600px at 60% 120%, rgba(81, 12, 118, 0.45), transparent 60%),
    var(--bg);
  box-shadow: 0 30px 80px -30px rgba(16, 2, 23, 0.6);
}
.cc::before {
  content: '';
  position: absolute; inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px);
  background-size: 44px 44px;
  mask-image: radial-gradient(ellipse at 50% 0%, black 30%, transparent 75%);
}
.cc * { box-sizing: border-box; }
.cc button { font: inherit; color: inherit; background: none; border: 0; cursor: pointer; padding: 0; }
.cc ::-webkit-scrollbar { width: 8px; height: 8px; }
.cc ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 8px; }
.cc ::-webkit-scrollbar-track { background: transparent; }

.cc-display { font-family: 'Poppins_600SemiBold', system-ui, sans-serif; letter-spacing: -0.01em; }
.cc-num { font-family: 'Poppins_600SemiBold', system-ui, sans-serif; font-variant-numeric: tabular-nums; }
.cc-muted { color: var(--muted); }
.cc-faint { color: var(--faint); }

/* ---------- Encabezado ---------- */
.cc-header {
  position: relative; z-index: 2;
  display: flex; align-items: center; gap: 18px;
  padding: 18px 22px 14px;
}
.cc-title { display: flex; flex-direction: column; min-width: 0; }
.cc-title h1 { margin: 0; font-size: 20px; line-height: 1.2; }
.cc-title span { font-size: 12.5px; color: var(--muted); }

.cc-live {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 12px 6px 10px; border-radius: 999px;
  font-size: 11px; letter-spacing: 0.14em; text-transform: uppercase;
  font-family: 'Poppins_600SemiBold', system-ui, sans-serif;
  background: var(--teal-soft); color: var(--teal);
  border: 1px solid rgba(42, 210, 197, 0.3);
}
.cc-live.offline { background: var(--red-soft); color: var(--red); border-color: rgba(255,61,106,0.3); }
.cc-live.connecting { background: var(--amber-soft); color: var(--amber); border-color: rgba(255,181,71,0.3); }
.cc-live-dot { position: relative; width: 8px; height: 8px; border-radius: 50%; background: currentColor; }
.cc-live:not(.offline) .cc-live-dot::after {
  content: ''; position: absolute; inset: -5px; border-radius: 50%;
  border: 2px solid currentColor; opacity: 0; animation: cc-ping 1.8s ease-out infinite;
}

.cc-spacer { flex: 1; }

.cc-segment {
  display: inline-flex; padding: 3px; border-radius: 12px;
  background: var(--panel); border: 1px solid var(--line);
}
.cc-segment button {
  padding: 6px 12px; border-radius: 9px; font-size: 12.5px; color: var(--muted);
  transition: background .2s, color .2s;
}
.cc-segment button:hover { color: var(--text); }
.cc-segment button.on { background: rgba(255,255,255,0.1); color: var(--text); box-shadow: inset 0 0 0 1px var(--line-strong); }

.cc-search {
  position: relative; display: flex; align-items: center; gap: 8px;
  width: 300px; padding: 9px 12px; border-radius: 12px;
  background: var(--panel); border: 1px solid var(--line);
  transition: border-color .2s, background .2s, width .3s;
}
.cc-search:focus-within { border-color: rgba(42,210,197,0.5); background: var(--panel-strong); width: 360px; }
.cc-search input {
  flex: 1; min-width: 0; border: 0; outline: none; background: transparent;
  color: var(--text); font: inherit; font-size: 13px;
}
.cc-search input::placeholder { color: var(--faint); }
.cc-kbd {
  font-size: 10.5px; padding: 2px 6px; border-radius: 6px;
  border: 1px solid var(--line-strong); color: var(--faint);
}
.cc-clock { font-size: 22px; line-height: 1; min-width: 104px; text-align: right; }
.cc-clock small { display: block; font-size: 11px; color: var(--muted); font-family: 'Poppins_400Regular', system-ui; margin-top: 4px; }

/* ---------- Indicadores ---------- */
.cc-kpis {
  position: relative; z-index: 1;
  display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)) minmax(280px, 1.6fr);
  gap: 10px; padding: 0 22px 14px;
}
.cc-kpi {
  position: relative; overflow: hidden;
  padding: 12px 14px; border-radius: 16px;
  background: var(--panel); border: 1px solid var(--line);
  transition: transform .2s, border-color .2s, background .2s;
}
.cc-kpi:hover { transform: translateY(-1px); border-color: var(--line-strong); }
.cc-kpi.clickable { cursor: pointer; }
.cc-kpi.on { border-color: rgba(42,210,197,0.45); background: rgba(42,210,197,0.07); }
.cc-kpi label { display: flex; align-items: center; gap: 6px; font-size: 11.5px; color: var(--muted); }
.cc-kpi strong { display: block; font-size: 28px; line-height: 1.15; margin-top: 4px; }
.cc-kpi.denied strong { color: var(--red); }
.cc-kpi.denied.on { border-color: rgba(255,61,106,0.45); background: rgba(255,61,106,0.07); }
.cc-kpi .cc-bump { animation: cc-bump .6s ease; display: inline-block; }

.cc-chart { padding: 10px 14px 8px; border-radius: 16px; background: var(--panel); border: 1px solid var(--line); }
.cc-chart-head { display: flex; justify-content: space-between; font-size: 11.5px; color: var(--muted); margin-bottom: 6px; }
.cc-bars { display: flex; align-items: flex-end; gap: 3px; height: 54px; }
.cc-bar { position: relative; flex: 1; display: flex; flex-direction: column-reverse; gap: 1px; height: 100%; }
.cc-bar i { display: block; border-radius: 3px; min-height: 0; transition: height .5s cubic-bezier(.2,.8,.2,1); }
.cc-bar i.g { background: linear-gradient(180deg, var(--teal), rgba(42,210,197,0.45)); }
.cc-bar i.d { background: var(--red); }
.cc-bar.now::after {
  content: ''; position: absolute; left: 50%; bottom: -6px; width: 4px; height: 4px; margin-left: -2px;
  border-radius: 50%; background: var(--teal); box-shadow: 0 0 10px var(--teal);
}
.cc-bar:hover i.g { filter: brightness(1.25); }
.cc-bar-tip {
  position: absolute; bottom: calc(100% + 6px); left: 50%; transform: translateX(-50%);
  white-space: nowrap; font-size: 11px; padding: 4px 8px; border-radius: 8px;
  background: #1D0F27; border: 1px solid var(--line-strong); opacity: 0; pointer-events: none;
  transition: opacity .15s; z-index: 5;
}
.cc-bar:hover .cc-bar-tip { opacity: 1; }
.cc-axis { display: flex; justify-content: space-between; font-size: 10px; color: var(--faint); margin-top: 6px; }

/* ---------- Cuerpo ---------- */
.cc-body {
  position: relative; z-index: 1;
  flex: 1; min-height: 0;
  display: grid; grid-template-columns: 270px minmax(0, 1fr); gap: 12px;
  padding: 0 22px 18px;
  transition: grid-template-columns .35s cubic-bezier(.2,.8,.2,1);
}
.cc-body.with-detail { grid-template-columns: 270px minmax(0, 1fr) 400px; }
.cc-col {
  min-height: 0; display: flex; flex-direction: column;
  border-radius: 18px; background: rgba(255,255,255,0.025); border: 1px solid var(--line);
  overflow: hidden;
}
.cc-col-head {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 14px; border-bottom: 1px solid var(--line);
  font-size: 12px; letter-spacing: 0.08em; text-transform: uppercase; color: var(--muted);
}
.cc-scroll { flex: 1; min-height: 0; overflow-y: auto; }

/* ---------- Zonas ---------- */
.cc-zone { padding: 10px 10px 4px; }
.cc-zone-name { display: flex; justify-content: space-between; padding: 0 6px 6px; font-size: 12px; color: var(--muted); }
.cc-point {
  position: relative; width: 100%; text-align: left;
  display: grid; grid-template-columns: 34px 1fr auto; align-items: center; gap: 10px;
  padding: 9px 10px; margin-bottom: 4px; border-radius: 12px;
  border: 1px solid transparent; transition: background .2s, border-color .2s;
}
.cc-point:hover { background: var(--panel-strong); }
.cc-point.on { background: rgba(42,210,197,0.08); border-color: rgba(42,210,197,0.35); }
.cc-point-icon {
  position: relative; width: 34px; height: 34px; border-radius: 11px;
  display: grid; place-items: center; background: var(--violet-soft); color: var(--violet);
}
.cc-radar {
  position: absolute; inset: -2px; border-radius: 13px; pointer-events: none;
  border: 2px solid var(--teal); opacity: 0;
}
.cc-radar.fire { animation: cc-radar 1.6s ease-out; }
.cc-radar.fire.denied { border-color: var(--red); }
.cc-point-name { font-size: 13px; line-height: 1.25; }
.cc-point-meta { font-size: 11px; color: var(--faint); margin-top: 1px; }
.cc-point-count { text-align: right; font-size: 15px; }
.cc-point-count small { display: block; font-size: 10.5px; color: var(--red); font-family: 'Poppins_400Regular', system-ui; }

/* ---------- Filtros ---------- */
.cc-filters {
  display: flex; align-items: center; flex-wrap: wrap; gap: 6px;
  padding: 10px 12px; border-bottom: 1px solid var(--line);
}
.cc-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 10px; border-radius: 999px; font-size: 12px; color: var(--muted);
  border: 1px solid var(--line); background: transparent; transition: all .2s;
}
.cc-chip:hover { color: var(--text); border-color: var(--line-strong); }
.cc-chip.on { color: var(--text); background: var(--violet-soft); border-color: rgba(197,116,241,0.45); }
.cc-chip .x { opacity: .6; }
.cc-menu {
  position: absolute; z-index: 20; top: calc(100% + 6px); left: 0; min-width: 200px;
  padding: 6px; border-radius: 14px; background: #1A0C24; border: 1px solid var(--line-strong);
  box-shadow: 0 20px 50px -10px rgba(0,0,0,0.6); animation: cc-pop .16s ease;
}
.cc-menu button {
  width: 100%; display: flex; align-items: center; gap: 8px; text-align: left;
  padding: 8px 10px; border-radius: 9px; font-size: 13px; color: var(--muted);
}
.cc-menu button:hover, .cc-menu button.on { background: var(--panel-strong); color: var(--text); }
.cc-count-pill { margin-left: auto; font-size: 11.5px; color: var(--faint); }

/* ---------- Bitácora ---------- */
.cc-day {
  position: sticky; top: 0; z-index: 3;
  padding: 10px 16px 6px; font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase;
  color: var(--faint); background: linear-gradient(180deg, rgba(14,6,20,0.96), rgba(14,6,20,0.7) 70%, transparent);
  backdrop-filter: blur(6px);
}
.cc-row {
  position: relative; width: 100%; text-align: left;
  display: grid; grid-template-columns: 78px 22px minmax(0, 1fr) auto; align-items: center; gap: 10px;
  padding: 10px 16px; transition: background .15s;
}
.cc-row:hover { background: rgba(255,255,255,0.035); }
.cc-row.selected { background: rgba(197,116,241,0.09); }
.cc-row.selected::before { content: ''; position: absolute; left: 0; top: 8px; bottom: 8px; width: 3px; border-radius: 3px; background: var(--violet); }
.cc-row.fresh { animation: cc-arrive .9s cubic-bezier(.2,.8,.2,1); }
.cc-row.fresh::after {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  background: linear-gradient(90deg, rgba(42,210,197,0.18), transparent 60%);
  animation: cc-fade 2.4s ease forwards;
}
.cc-row.fresh.denied::after { background: linear-gradient(90deg, rgba(255,61,106,0.2), transparent 60%); }
.cc-time { font-size: 13px; }
.cc-time small { display: block; font-size: 10.5px; color: var(--faint); font-family: 'Poppins_400Regular', system-ui; }
.cc-node { position: relative; height: 100%; display: flex; align-items: center; justify-content: center; }
.cc-node::before { content: ''; position: absolute; top: -10px; bottom: -10px; width: 1px; background: var(--line); }
.cc-node i {
  position: relative; width: 11px; height: 11px; border-radius: 50%;
  background: var(--teal); box-shadow: 0 0 0 3px rgba(42,210,197,0.18), 0 0 14px rgba(42,210,197,0.5);
}
.cc-node i.denied { background: var(--red); box-shadow: 0 0 0 3px rgba(255,61,106,0.18), 0 0 14px rgba(255,61,106,0.5); }
.cc-node i.unknown, .cc-node i.pending { background: var(--amber); box-shadow: 0 0 0 3px rgba(255,181,71,0.18); }
.cc-who { min-width: 0; }
.cc-who b { display: block; font-size: 14px; font-weight: normal; font-family: 'Poppins_500Medium', system-ui; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cc-who span { display: block; font-size: 12px; color: var(--muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-top: 1px; }
.cc-tags { display: flex; align-items: center; gap: 6px; justify-content: flex-end; }
.cc-method {
  width: 28px; height: 28px; border-radius: 9px; display: grid; place-items: center;
  background: var(--panel-strong); color: var(--muted); border: 1px solid var(--line);
}
.cc-badge {
  display: inline-flex; align-items: center; gap: 6px; white-space: nowrap;
  padding: 5px 10px; border-radius: 999px; font-size: 12px;
  background: var(--teal-soft); color: var(--teal);
}
.cc-badge.denied { background: var(--red-soft); color: var(--red); }
.cc-badge.unknown, .cc-badge.pending { background: var(--amber-soft); color: var(--amber); }
.cc-dir { font-size: 11px; color: var(--faint); width: 52px; text-align: right; }
.cc-flag {
  display: inline-flex; align-items: center; gap: 4px; margin-left: 8px; vertical-align: 2px;
  padding: 1px 7px; border-radius: 999px; font-size: 10.5px; font-family: 'Poppins_500Medium', system-ui;
  background: var(--red-soft); color: var(--red); border: 1px solid rgba(255,61,106,0.3);
}
.cc-note { margin-top: 10px; padding: 8px 10px; border-radius: 10px; font-size: 12px; background: var(--amber-soft); color: var(--amber); }

.cc-ring { width: 16px; height: 16px; transform: rotate(-90deg); }
.cc-ring circle { fill: none; stroke-width: 3; }
.cc-ring .track { stroke: rgba(255,255,255,0.12); }
.cc-ring .left { stroke: var(--amber); stroke-dasharray: 44; stroke-dashoffset: 0; animation-name: cc-drain; animation-timing-function: linear; animation-fill-mode: forwards; }

.cc-loadmore { height: 1px; }
.cc-empty { display: grid; place-items: center; text-align: center; padding: 60px 20px; color: var(--muted); }
.cc-empty-radar { position: relative; width: 120px; height: 120px; margin-bottom: 18px; }
.cc-empty-radar span { position: absolute; inset: 0; border-radius: 50%; border: 1px solid rgba(42,210,197,0.35); animation: cc-ripple 3s ease-out infinite; }
.cc-empty-radar span:nth-child(2) { animation-delay: 1s; }
.cc-empty-radar span:nth-child(3) { animation-delay: 2s; }
.cc-empty-radar b { position: absolute; inset: 50%; width: 10px; height: 10px; margin: -5px; border-radius: 50%; background: var(--teal); box-shadow: 0 0 18px var(--teal); }
.cc-skel { height: 52px; margin: 6px 16px; border-radius: 12px; background: linear-gradient(90deg, var(--panel), var(--panel-strong), var(--panel)); background-size: 200% 100%; animation: cc-shimmer 1.4s infinite; }

/* ---------- Detalle ---------- */
.cc-detail { animation: cc-slide .35s cubic-bezier(.2,.8,.2,1); }
.cc-detail-hero { padding: 18px 18px 16px; border-bottom: 1px solid var(--line); position: relative; overflow: hidden; }
.cc-detail-hero::before {
  content: ''; position: absolute; inset: 0; opacity: .9; pointer-events: none;
  background: radial-gradient(420px 160px at 0% 0%, rgba(42,210,197,0.22), transparent 70%);
}
.cc-detail-hero.denied::before { background: radial-gradient(420px 160px at 0% 0%, rgba(255,61,106,0.24), transparent 70%); }
.cc-detail-hero.unknown::before, .cc-detail-hero.pending::before { background: radial-gradient(420px 160px at 0% 0%, rgba(255,181,71,0.22), transparent 70%); }
.cc-detail-close { position: absolute; top: 12px; right: 12px; width: 30px; height: 30px; border-radius: 10px; display: grid; place-items: center; background: var(--panel-strong); z-index: 1; }
.cc-detail-hero h2 { position: relative; margin: 10px 0 2px; font-size: 22px; line-height: 1.2; }
.cc-detail-hero p { position: relative; margin: 0; color: var(--muted); font-size: 13px; }
.cc-section { padding: 14px 18px; border-bottom: 1px solid var(--line); }
.cc-section h3 { margin: 0 0 10px; font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: var(--faint); font-weight: normal; }
.cc-facts { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 14px; }
.cc-fact span { display: block; font-size: 11px; color: var(--faint); }
.cc-fact b { display: block; font-size: 13.5px; font-weight: normal; margin-top: 2px; word-break: break-word; }

.cc-steps { position: relative; margin: 0; padding: 0; list-style: none; }
.cc-steps::before { content: ''; position: absolute; left: 7px; top: 8px; bottom: 8px; width: 1px; background: var(--line-strong); }
.cc-step { position: relative; display: grid; grid-template-columns: 16px 1fr auto; gap: 12px; padding: 7px 0; animation: cc-pop .3s ease both; }
.cc-step-dot { width: 15px; height: 15px; margin-top: 2px; border-radius: 50%; background: var(--bg); border: 2px solid var(--violet); z-index: 1; }
.cc-step-dot.ok { border-color: var(--teal); background: var(--teal); box-shadow: 0 0 10px rgba(42,210,197,0.6); }
.cc-step-dot.bad { border-color: var(--red); background: var(--red); }
.cc-step-dot.dup { border-color: var(--faint); border-style: dashed; }
.cc-step b { display: block; font-size: 13px; font-weight: normal; }
.cc-step small { display: block; font-size: 11.5px; color: var(--muted); margin-top: 1px; }
.cc-step em { font-style: normal; font-size: 11.5px; color: var(--faint); white-space: nowrap; }
.cc-raw { margin-top: 6px; padding: 8px 10px; border-radius: 10px; background: rgba(0,0,0,0.35); font: 11px/1.45 ui-monospace, Menlo, monospace; color: #CDB8DA; white-space: pre-wrap; word-break: break-all; }
.cc-link { color: var(--teal); font-size: 12px; }

/* ---------- Animaciones ---------- */
@keyframes cc-ping { 0% { transform: scale(.6); opacity: .9; } 100% { transform: scale(1.8); opacity: 0; } }
@keyframes cc-radar { 0% { transform: scale(.85); opacity: 1; } 100% { transform: scale(1.5); opacity: 0; } }
@keyframes cc-arrive { 0% { transform: translateY(-14px); opacity: 0; } 100% { transform: none; opacity: 1; } }
@keyframes cc-fade { 0% { opacity: 1; } 100% { opacity: 0; } }
@keyframes cc-bump { 0% { transform: scale(1); } 40% { transform: scale(1.18); color: var(--teal); } 100% { transform: scale(1); } }
@keyframes cc-pop { from { opacity: 0; transform: translateY(-4px) scale(.98); } to { opacity: 1; transform: none; } }
@keyframes cc-slide { from { opacity: 0; transform: translateX(24px); } to { opacity: 1; transform: none; } }
@keyframes cc-drain { to { stroke-dashoffset: 44; } }
@keyframes cc-ripple { 0% { transform: scale(.2); opacity: 1; } 100% { transform: scale(1); opacity: 0; } }
@keyframes cc-shimmer { to { background-position: -200% 0; } }

@media (max-width: 1280px) {
  .cc-kpis { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .cc-chart { grid-column: span 3; }
  .cc-search { width: 220px; }
  .cc-search:focus-within { width: 260px; }
}
@media (max-width: 1100px) {
  .cc-body, .cc-body.with-detail { grid-template-columns: minmax(0, 1fr); }
  .cc-rail { display: none; }
  .cc-body.with-detail .cc-feed { display: none; }
}
@media (prefers-reduced-motion: reduce) {
  .cc *, .cc *::before, .cc *::after { animation: none !important; transition: none !important; }
}
`;
